/** 키보드가 내려가는 동안 원래 위치로 다시 맞추는 시점(풀고 나서 ms, 0 = 다음 프레임) */
const KEYBOARD_RESYNC_DELAYS_MS: number[] = [0, 60, 120, 360, 720];

const isTextField = (target: EventTarget | null): boolean => {
    if (target instanceof HTMLTextAreaElement) return !target.readOnly;
    if (target instanceof HTMLInputElement) return !target.readOnly && !['checkbox', 'radio', 'button', 'submit', 'range', 'file', 'hidden'].includes(target.type);
    return target instanceof HTMLElement && target.isContentEditable;
};

/** 풀고 나서 키보드가 내려가는 동안 원래 위치(scrollY)로 다시 맞춘다 */
const resyncAfterKeyboard = (scrollY: number): void => {
    const href: string = window.location.href;
    let cancelled: boolean = false;
    const cancel = (): void => {
        cancelled = true;
        window.removeEventListener('touchstart', cancel, true);
        window.removeEventListener('wheel', cancel, true);
        window.removeEventListener('keydown', cancel, true);
    };
    window.addEventListener('touchstart', cancel, true);
    window.addEventListener('wheel', cancel, true);
    window.addEventListener('keydown', cancel, true);
    const resync = (last: boolean): void => {
        if (!cancelled && window.location.href === href && document.body.dataset.scrollLock !== 'true' && Math.round(window.scrollY) === Math.round(scrollY)) {
            window.scrollTo(0, scrollY);
        }
        if (last) cancel();
    };
    // 0 은 다음 화면 그리기 직전(풀자마자 어긋난 화면이 한 장이라도 덜 보이게)
    KEYBOARD_RESYNC_DELAYS_MS.forEach((delay: number, index: number) => {
        const last: boolean = index === KEYBOARD_RESYNC_DELAYS_MS.length - 1;
        if (delay === 0) window.requestAnimationFrame(() => resync(last));
        else window.setTimeout(() => resync(last), delay);
    });
};

/**
 * 시트·메뉴가 열린 동안 뒤 화면(문서)이 스크롤되지 않게 몸통을 잠근다 — 공용 시트(FilterPanel)·AI 시트·헤더 메뉴가 같이 쓴다.
 *
 * iOS 는 overflow:hidden 만으로는 뒤 화면이 계속 밀리므로 body 를 `position:fixed; top:-scrollY` 로 묶는다(문서 높이가 화면만 해져 밀 곳이 없다).
 * body 에는 overflow 를 걸지 않고 position 만 바꾼다. 2026-10-07 까지는 html 이 overflow-x:clip 이라 body 의 overflow:hidden 이 화면으로
 * 넘어가지 않고 body 자신을 잘림(스크롤) 상자로 만들어, 스크롤한 채 시트를 열면 sticky 머리줄·탭 줄이 내린 만큼 화면 위로 사라졌다
 * (운영 앱 마이페이지 로그아웃 시트, MUI 창의 로그인 안내 창). 지금은 html 에서 clip 을 빼(globalStyles) body 의 overflow 가 화면으로
 * 넘어가므로 MUI 기본 잠금(body overflow:hidden)도 머리줄을 밀지 않는다 — 그래도 iOS 는 overflow 만으로 뒤 화면이 밀리므로 여기서는 고정 방식을 쓴다.
 * (body 를 화면 높이 스크롤 상자로 두고 그 안을 scrollY 만큼 스크롤하는 방법은 크롬(안드로이드 앱)에서 시트의 뒤로가기 칸을 되돌린 뒤
 *  브라우저 스크롤 복원이 늦게 와 닫은 화면을 맨 위로 올려 버려 쓰지 않는다.)
 *
 * - 이미 잠겨 있으면(`body[data-scroll-lock]` — 시트 위 시트, 대화방 키보드 잠금 등) 아무것도 하지 않는다.
 * - 당겨서 새로고침·스와이프 뒤로가기는 `body[data-scroll-lock]` 표시를 보고 쉰다.
 * - 풀면 body 스타일을 되돌리고 원래 스크롤 위치로 돌아간다.
 * - 잠근 동안 글자 칸에 포커스가 간 적이 있으면(화면 키보드) 풀고 나서 키보드가 다 내려갈 때까지 몇 번 더 원래 위치로 맞춘다 — 아이폰 앱 웹뷰가
 *   키보드를 내리면서 네이티브 스크롤을 잠근 동안의 0 으로 되돌려, 문서(scrollY)는 원래 위치인데 화면은 맨 위·빈 화면으로 어긋났다
 *   (2026-10-07 새 앱, 앱을 켠 뒤 첫 키보드 — 탈퇴 비밀번호 시트를 내린 채 열고 키보드를 띄운 채 [취소]). 같은 위치로 scrollTo 를 다시 부르면 맞춰진다.
 *   그 사이 사용자가 화면을 만지거나, 주소가 바뀌거나, 문서 위치가 바뀌었거나, 다시 잠겼으면 하지 않는다.
 *
 * @returns 잠금을 푸는 함수(이미 잠겨 있어 건드리지 않았으면 undefined)
 */
export const lockBodyScroll = (): (() => void) | undefined => {
    const body: HTMLElement = document.body;
    if (body.dataset.scrollLock === 'true') return undefined;
    const scrollY: number = window.scrollY;
    let typed: boolean = isTextField(document.activeElement);
    const onFocusIn = (event: FocusEvent): void => {
        if (isTextField(event.target)) typed = true;
    };
    document.addEventListener('focusin', onFocusIn, true);
    const previous = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.dataset.scrollLock = 'true';
    return () => {
        document.removeEventListener('focusin', onFocusIn, true);
        body.style.position = previous.position;
        body.style.top = previous.top;
        body.style.left = previous.left;
        body.style.right = previous.right;
        body.style.width = previous.width;
        delete body.dataset.scrollLock;
        window.scrollTo(0, scrollY);
        if (typed) resyncAfterKeyboard(scrollY);
    };
};
